import { Query, useQueryClient } from "react-query";
import { Activity, ActivityPage } from "../../@types/activities.ts";

const isStickyActivitiesQuery = (query: Query) => {
  const key = query.queryKey;
  return typeof key === "string" && key.endsWith("/sticky/activities");
};

export function useUpdateActivityInCache() {
  const queryClient = useQueryClient();

  const updateActivity = (
    activityId?: string,
    updater?: (activity: Activity) => Activity,
  ) => {
    if (!activityId || !updater) return;
    queryClient.setQueriesData({ queryKey: ["activities"] }, (data: any) => {
      if (!data?.pages) return data;

      return {
        ...data,
        pages: data.pages.map((page: ActivityPage) => ({
          ...page,
          data: page.data.map((activity: Activity) =>
            activity.id === activityId ? updater(activity) : activity,
          ),
        })),
      };
    });

    // Sticky activities live in a separate flat-array cache that the
    // "activities" prefix above doesn't match. If the updater nulls sticky,
    // drop the entry from sticky caches — leaving it with sticky:null would
    // render as a ghost pinned-duplicate until the next refetch.
    queryClient.setQueriesData(
      { predicate: isStickyActivitiesQuery },
      (data: any) => {
        if (!Array.isArray(data)) return data;
        return data.flatMap((activity: Activity) => {
          if (activity.id !== activityId) return [activity];
          const updated = updater(activity);
          return updated.sticky === null ? [] : [updated];
        });
      },
    );

    // Also update the single activity query used on the detail page
    const singleActivityData = queryClient.getQueryData<Activity>(
      `/activities/${activityId}`,
    );
    if (singleActivityData) {
      queryClient.setQueryData(
        `/activities/${activityId}`,
        updater(singleActivityData),
      );
    }
  };

  const prependActivity = (newActivity: Activity) => {
    queryClient.setQueriesData(
      { queryKey: ["activities"], exact: false },
      (oldData: any) => {
        if (!oldData?.pages?.length) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page: any, i: number) =>
            i === 0 ? { ...page, data: [newActivity, ...page.data] } : page,
          ),
        };
      },
    );
  };

  const removeActivity = (activityId: string) => {
    if (!activityId) return;

    queryClient.setQueriesData({ queryKey: ["activities"] }, (data: any) => {
      if (!data?.pages) return data;

      return {
        ...data,
        pages: data.pages.map((page: ActivityPage) => ({
          ...page,
          data: page.data.filter(
            (activity: Activity) => activity.id !== activityId,
          ),
        })),
      };
    });

    queryClient.setQueriesData(
      { predicate: isStickyActivitiesQuery },
      (data: any) => {
        if (!Array.isArray(data)) return data;
        return data.filter((activity: Activity) => activity.id !== activityId);
      },
    );
  };

  return { updateActivity, prependActivity, removeActivity };
}
